import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter(
    (a) => currentCategory && a.category === currentCategory.name
  );

  if (!currentCategory) {
    return (
      <>
        <SEOHead title="分类未找到" description="抱歉，您访问的分类不存在。" />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors duration-200">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 文爱社区`,
    description: currentCategory.description,
    url: `https://luckilypink.com.cn/category/${currentCategory.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '文爱社区',
      url: 'https://luckilypink.com.cn',
    },
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 文爱社区分类浏览`}
        description={currentCategory.description}
        keywords={`${currentCategory.name},文爱社区,文字情感社区,文爱交流平台`}
        canonicalUrl={`https://luckilypink.com.cn/category/${currentCategory.slug}`}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="min-h-screen pt-20 pb-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>

          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <i className={`${currentCategory.icon} text-lg text-primary-600 w-5 h-5 flex items-center justify-center`}></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {currentCategory.name}
              </h1>
            </div>
            <p className="text-foreground-500 text-sm md:text-base">
              {currentCategory.description}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  cat.slug === currentCategory.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-700'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article, idx) => (
                <article
                  key={article.id}
                  className="bg-background-100 rounded-lg overflow-hidden group cursor-pointer animate-fade-in-up"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <Link to={`/article/${article.id}`} className="block">
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={`${article.title} - 文爱社区${currentCategory.name}分类`}
                      />
                    </div>
                    <div className="p-4">
                      <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 mb-3 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-400">{article.date}</span>
                        <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类下暂无文章，敬请期待。</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}